import { Box, Button, DataList, Dialog, Text } from '@chakra-ui/react';
import { useParams } from '@tanstack/react-router';
import DOMPurify from 'dompurify';
import parse from 'html-react-parser';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaPlus } from 'react-icons/fa';

import type { IndicatorSuggestionDetailsPublic } from '@/client';
import { SelectCommitment } from '@/components/common/SelectCommitment';
import SelectUnits from '@/components/common/SelectUnits';

import { useIndicatorSuggestionAddForm } from './useIndicatorSuggestionAddForm';

const LibraryAddIndicatorDialog = ({
  suggestion,
  onSuccess,
}: {
  suggestion?: IndicatorSuggestionDetailsPublic | null;
  onSuccess?: () => void;
}) => {
  const { t } = useTranslation();
  const { slug } = useParams({ strict: false });
  const [isOpen, setIsOpen] = useState(false);

  const form = useIndicatorSuggestionAddForm({
    slug,
    suggestion,
    onSuccess: () => resetModalBehavior(),
  });

  const resetModalBehavior = () => {
    form.reset();
    setIsOpen(false);
    onSuccess?.();
  };

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={(e) => {
        setIsOpen(e.open);
      }}
      placement="center"
      size="lg"
    >
      <Dialog.Backdrop />
      <Dialog.Trigger asChild>
        <Button variant="outline">
          <FaPlus /> {t('indicator.add.btn')}
        </Button>
      </Dialog.Trigger>
      <Dialog.Content
        as="form"
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
      >
        <Dialog.CloseTrigger onClick={() => resetModalBehavior()} />

        <Dialog.Header gap={'0'}>
          <Dialog.Title>{t('indicator.drawer.add.title')}</Dialog.Title>
        </Dialog.Header>
        <Dialog.Body>
          <DataList.Root orientation="horizontal" divideY="1px">
            <DataList.Item pt="4" w={'100%'}>
              <DataList.ItemLabel>{t('indicator.columnsHeader.indicatorName')}</DataList.ItemLabel>
              <DataList.ItemValue>
                <Text>{suggestion?.name}</Text>
              </DataList.ItemValue>
            </DataList.Item>

            <DataList.Item pt="6" w={'100%'}>
              <DataList.ItemLabel>{t('indicator.columnsHeader.description')}</DataList.ItemLabel>
              <DataList.ItemValue>
                {suggestion?.description ? (
                  <Text className="ql-editor ql-viewer" as="div">
                    {parse(DOMPurify.sanitize(suggestion.description))}
                  </Text>
                ) : (
                  <Text color="gray.500" textStyle="sm">
                    N/R
                  </Text>
                )}
              </DataList.ItemValue>
            </DataList.Item>
            <DataList.Item pt="4" w={'100%'}>
              <DataList.ItemLabel>{t('indicator.columnsHeader.unit')}</DataList.ItemLabel>
              {suggestion?.lt_indicator_category === 'simple' && (
                <DataList.ItemValue>
                  <form.Field name="unit">
                    {(field) => (
                      <Box w={'100%'}>
                        <SelectUnits
                          slug={slug}
                          unitListString={suggestion?.lt_indicator_other_unit ?? ''}
                          value={field.state.value !== '' ? [field.state.value] : []}
                          onChange={(e) => field.handleChange(e.length > 0 ? e[0] : '')}
                        />
                        {!field.state.meta.isValid && (
                          <Text color="red.500" fontSize="sm" mt={2} fontStyle={'italic'}>
                            {field.state.meta.errors.join(', ')}
                          </Text>
                        )}
                      </Box>
                    )}
                  </form.Field>
                </DataList.ItemValue>
              )}
              {suggestion?.lt_indicator_category === 'composite' && (
                <DataList.ItemValue flexWrap={'wrap'} gap={4}>
                  {form.getFieldValue('units').map((_, index) => (
                    <form.Field key={index} name={`units[${index}].unit`}>
                      {(field) => {
                        const indicator = suggestion?.formula_units_per_indicator?.[index];

                        if (!indicator) {
                          return <Text>N/R</Text>;
                        }

                        return (
                          <Box w={'100%'}>
                            <SelectUnits
                              slug={slug}
                              label={indicator.suggestion_name}
                              unitListString={indicator.suggestion_units ?? ''}
                              value={field.state.value !== '' ? [field.state.value] : []}
                              onChange={(e) => field.handleChange(e.length > 0 ? e[0] : '')}
                            />
                            {!field.state.meta.isValid && (
                              <Text color="red.500" fontSize="sm" mt={2} fontStyle={'italic'}>
                                {field.state.meta.errors.join(', ')}
                              </Text>
                            )}
                          </Box>
                        );
                      }}
                    </form.Field>
                  ))}
                </DataList.ItemValue>
              )}
            </DataList.Item>
            <DataList.Item pt="3" w={'100%'} borderTopWidth={1}>
              <DataList.ItemLabel lineHeight={4}>
                {t('indicator.columnsHeader.pillarName')}
                <br />
                {t('indicator.columnsHeader.commitmentName')}
              </DataList.ItemLabel>
              <DataList.ItemValue>
                <form.Field name="commitment_id">
                  {(field) => (
                    <SelectCommitment
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e)}
                      onLine={false}
                    />
                  )}
                </form.Field>
              </DataList.ItemValue>
            </DataList.Item>
          </DataList.Root>
        </Dialog.Body>

        <Dialog.Footer>
          <Button onClick={() => resetModalBehavior()} variant="outline">
            {t('ui.cancel')}
          </Button>
          <form.Subscribe selector={(state) => [state.canSubmit, state.isSubmitting]}>
            {([canSubmit, isSubmitting]) => (
              <Button type="submit" disabled={!canSubmit}>
                {t('ui.save')} {isSubmitting ? '...' : ''}
              </Button>
            )}
          </form.Subscribe>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog.Root>
  );
};

export default LibraryAddIndicatorDialog;
